import { MaasRuntimeType, MaasWorkloadType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { hasFormFieldValue } from '@/delivery-type-plugin/platform/scene-layout/utils/resolveListModuleVisible';

import type { DeploySceneConfig } from '../../platform/scenes/types';
import { MI_MODULES } from './modules';

function readPresetRecord(snapshot?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!snapshot) return undefined;
  if (snapshot.data && typeof snapshot.data === 'object' && !Array.isArray(snapshot.data)) {
    return snapshot.data as Record<string, unknown>;
  }
  return snapshot;
}

/** SKU 预置或 envelope 内是否开启 PD 分离 */
export function isModelInstancePresetPdSeparation(snapshot?: Record<string, unknown>): boolean {
  const flat = readPresetRecord(snapshot);
  return flat?._inferencePdSeparation === true;
}

/** 自定义规格 SKU 预置了 Prefill/Decode 拓扑但未写 PD 标记时的兜底 */
export function hasModelInstancePdSegmentPreset(snapshot?: Record<string, unknown>): boolean {
  const flat = readPresetRecord(snapshot);
  if (!flat) return false;
  const segmentKeys = [
    'prefillRunMode',
    'prefillMasterNodes',
    'prefillWorkerNodes',
    'prefillReplicas',
    'decodeRunMode',
    'decodeMasterNodes',
    'decodeWorkerNodes',
    'decodeReplicas',
  ] as const;
  return segmentKeys.some((key) => hasFormFieldValue(flat, key));
}

/**
 * 下单折叠是否按 PD 拆 Prefill / Decode。
 * - `formPdSeparation === true | false`：以表单为准（用户刚切换部署方式）
 * - `formPdSeparation` 为 `undefined`：watch 尚未跟上时，用 SKU 预置 PD 标记 / Prefill·Decode 拓扑兜底
 */
export function resolveModelInstancePdSeparation(options: { formPdSeparation?: boolean; preset?: Record<string, unknown> }): boolean {
  if (options.formPdSeparation === true) return true;
  if (options.formPdSeparation === false) return false;
  if (isModelInstancePresetPdSeparation(options.preset)) return true;
  if (hasModelInstancePdSegmentPreset(options.preset)) return true;
  return false;
}

export const MI_ALL_MODULES = MI_MODULES.map((m) => m.key);

/** SKU 可见模块（不含基本信息；含运行配置，与直建一致） */
export const MI_SKU_MODULE_KEYS = ['runtimeConfig', 'modelStorage', 'gatewayConfig', 'kvOffload', 'nodeGroups', 'service'] as const;

export type MiSkuModuleKey = (typeof MI_SKU_MODULE_KEYS)[number];

export const MI_SKU_MODULE_LIST: string[] = [...MI_SKU_MODULE_KEYS];

/**
 * 下单可见模块：与直建勾选块一致（MI_DIRECT_MERGED 去掉 baseInfo）。
 * `nodeGroups` 在 PD 分离时由下单页拆成 Prefill / Decode 两个折叠。
 */
export const MI_ORDER_MODULE_KEYS = [
  'runtimeConfig',
  'modelStorage',
  'storage',
  'gatewayConfig',
  'kvOffload',
  'nodeGroups',
  'service',
] as const;

export type MiOrderModuleKey = (typeof MI_ORDER_MODULE_KEYS)[number];

export const MI_ORDER_MODULE_LIST: string[] = [...MI_ORDER_MODULE_KEYS];

/** 下单 deliveryMetadata 固定 runtime（与创建页可选运行时无关） */
export const MI_ORDER_DELIVERY_RUNTIME = {
  type: MaasRuntimeType.ContainerInstance,
  workload: MaasWorkloadType.ContainerInstance,
} as const;

/** 下单模块标题 i18n key（L3 用 t(key, mod.title) 回退） */
export const MI_ORDER_MODULE_TITLE: Record<string, string> = {
  runtimeConfig: 'data-center:maas.runtime.config',
  modelStorage: 'data-center:maas.section.modelStorage',
  storage: 'data-center:maas.section.storage',
  kvOffload: 'data-center:maas.cache.node.config',
  nodeGroups: 'data-center:maas.inference.node.config',
  /** PD 分离：与直建 Prefill / Decode 勾选块标题一致 */
  'nodeGroups:prefill': 'data-center:maas.inference.prefill.node.config',
  'nodeGroups:decode': 'data-center:maas.inference.decode.node.config',
  gatewayConfig: 'data-center:maas.gateway.node.config',
  service: 'data-center:access.config.service.endpoint',
};

/** 下单推理节点折叠段：普通部署 / PD Prefill / PD Decode */
export type MiOrderNodeGroupsSegment = 'default' | 'prefill' | 'decode';

export interface MiOrderFoldSection {
  /** React list key；PD 时为 nodeGroups:prefill / nodeGroups:decode */
  key: string;
  /** 插件模块 key（PD 折叠仍为 nodeGroups） */
  moduleKey: string;
  /** 仅 nodeGroups：指定渲染拓扑 */
  nodeGroupsSegment?: MiOrderNodeGroupsSegment;
  titleKey?: string;
  titleFallback: string;
  /** 折叠默认展开：对齐直建锁定模块（PD 两段均按 nodeGroups） */
  lockModuleKey: string;
}

/** 下单折叠块列表：一个折叠对应直建一个勾选块；PD 时拆分推理节点 */
export function resolveMiOrderFoldSections(options: {
  moduleKeys?: readonly string[];
  pdSeparation: boolean;
  moduleTitleFallback?: (moduleKey: string) => string;
}): MiOrderFoldSection[] {
  const moduleKeys = options.moduleKeys ?? MI_ORDER_MODULE_LIST;
  const fallback = options.moduleTitleFallback ?? ((key: string) => key);
  const sections: MiOrderFoldSection[] = [];

  for (const moduleKey of moduleKeys) {
    if (moduleKey === 'nodeGroups' && options.pdSeparation) {
      sections.push({
        key: 'nodeGroups:prefill',
        moduleKey: 'nodeGroups',
        nodeGroupsSegment: 'prefill',
        titleKey: MI_ORDER_MODULE_TITLE['nodeGroups:prefill'],
        titleFallback: '预填充组件（Prefill）配置',
        lockModuleKey: 'nodeGroups',
      });
      sections.push({
        key: 'nodeGroups:decode',
        moduleKey: 'nodeGroups',
        nodeGroupsSegment: 'decode',
        titleKey: MI_ORDER_MODULE_TITLE['nodeGroups:decode'],
        titleFallback: '解码组件（Decode）配置',
        lockModuleKey: 'nodeGroups',
      });
      continue;
    }

    sections.push({
      key: moduleKey,
      moduleKey,
      /** nodeGroups 非 PD 时不传 segment，交给内层按表单 `_inferencePdSeparation` 渲染 */
      nodeGroupsSegment: undefined,
      titleKey: MI_ORDER_MODULE_TITLE[moduleKey],
      titleFallback: fallback(moduleKey),
      lockModuleKey: moduleKey,
    });
  }

  return sections;
}

export function filterMiSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const byKey = new Map(modules.map((mod) => [mod.key, mod]));
  return MI_SKU_MODULE_KEYS.map((key) => byKey.get(key)).filter((mod): mod is T => !!mod);
}

export const MI_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...MI_ALL_MODULES],
    linkage: false,
  },
  sku: {
    modules: [...MI_SKU_MODULE_KEYS],
    linkage: false,
  },
  order: {
    modules: [...MI_ORDER_MODULE_KEYS],
    linkage: false,
  },
};

export function resolveMiSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return MI_SCENES[scene ?? 'direct'];
}

export interface MiDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

/** 直建合并「资源配置」步模块 */
export const MI_DIRECT_MERGED_MODULES = [
  'baseInfo',
  'runtimeConfig',
  'modelStorage',
  'storage',
  'gatewayConfig',
  'kvOffload',
  'nodeGroups',
  'service',
] as const;

export type MiDirectMergedModuleKey = (typeof MI_DIRECT_MERGED_MODULES)[number];

/**
 * 环境变量与任务配置已迁入各节点拓扑，不得再作为资源配置最外层模块渲染。拓扑级 initCommand 已移除。
 * filterMiDirectMergedModules 会强制排除，防止历史 MI_DIRECT_MERGED 或 custom keys 误带回。
 */
export const MI_RESOURCE_STEP_EXCLUDED_MODULE_KEYS = ['initCommand', 'envVars', 'taskConfig'] as const;

/** 直建步骤：推理任务配置嵌在各节点配置模块内，不单独成步 */
export const MI_DIRECT_CREATE_STEPS: MiDirectCreateStep[] = [
  { key: 'resource', title: '资源配置', modules: [...MI_DIRECT_MERGED_MODULES] },
];

export function filterMiDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const excluded = new Set<string>(MI_RESOURCE_STEP_EXCLUDED_MODULE_KEYS);
  const byKey = new Map(modules.map((mod) => [mod.key, mod]));
  return MI_DIRECT_MERGED_MODULES.filter((key) => !excluded.has(key))
    .map((key) => byKey.get(key))
    .filter((mod): mod is T => !!mod);
}

/** 直建 / 复用场景自定义模块顺序时，剔除不得出现在最外层的模块 key */
export function filterMiDirectResourceStepModuleKeys(keys: readonly string[]): string[] {
  const excluded = new Set<string>(MI_RESOURCE_STEP_EXCLUDED_MODULE_KEYS);
  return keys.filter((key) => !excluded.has(key));
}
